import React, { useEffect, useMemo, useState } from 'react';
import { InputWithIcon, SelectInput } from 'components/input';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import { LabelRowWithActions } from '../../../searchAndFilter/components';
import {
  usePerformSearch,
  useSearchDisplayFields,
  useSearchResult,
  useSearchTargetRelations,
  useSelectedSearchTarget,
} from 'redux/hooks/searchnFilter';
import {
  ISearchDisplayFieldDTO,
  ISearchFieldDTO,
  ISearchTargetDTO,
  SearchDisplayFieldStatus,
  SearchFieldDTO,
  SearchTargetDTO,
} from 'business/models/Models';
import CollapsiblePanel from 'components/containers/collapsibleContainer';
import {
  addDisplayField,
  removeDisplayField,
  toggleDisplayField,
} from 'redux/reducers/searchnFilterReducer';
import { useRMDispatch } from 'redux/hooks';
import { useLocation } from 'react-router';
import { useDefaultSelectedSearchFields } from 'redux/hooks/common';
import { searchFieldsFilters } from '../constants';
import { useModalHandlers } from 'components/modal';
import { FmlxButton } from 'fmlx-common-ui';

const PanelContentContainer = (props) => {
  const [searchFieldQuery, setSearchFieldQuery] = useState('');
  const [searchFieldFilter, setSearchFieldFilter] = useState(searchFieldsFilters[0]);
  const targetRelations = useSearchTargetRelations();
  const selectedSearchTargetId: number = useSelectedSearchTarget();
  const displayFields = useSearchDisplayFields();
  const defaultSelectedFields = useDefaultSelectedSearchFields();
  const searchResult = useSearchResult();
  const { showModal, hideModal } = useModalHandlers();
  const [searchAgain, setSearchAgain] = useState(false);
  const performSearch = usePerformSearch();
  const dispatch = useRMDispatch();
  const location = useLocation();
  const isResultTab = location.pathname.includes('/result');
  const [pendingSelections, setPendingSelections] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (searchAgain) {
      performSearch();
      setSearchAgain(false);
    }
  }, [searchAgain]);

  const handleSelectionChange = (fieldId: number) => {
    const existingFieldIndex = displayFields.findIndex(
      (df) => df.searchFieldId === fieldId,
    );

    if (existingFieldIndex >= 0) {
      dispatch(removeDisplayField(existingFieldIndex));

      setPendingSelections((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fieldId);
        return newSet;
      });
    } else {
      setPendingSelections((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(fieldId)) {
          newSet.delete(fieldId);
        } else {
          newSet.add(fieldId);
        }
        return newSet;
      });
    }
  };

  const visibilityChangeHandler = (fieldId: number) => {
    dispatch(toggleDisplayField(fieldId));
  };

  const targetsRelationsOptionsList = useMemo(() => {
    const regexp = new RegExp(searchFieldQuery, 'i');
    if (!selectedSearchTargetId || !targetRelations[selectedSearchTargetId]) {
      return [];
    }

    let _currentRelatedTarget: ISearchTargetDTO[] = [
      ...targetRelations[selectedSearchTargetId],
    ];
    if (searchFieldQuery !== '') {
      _currentRelatedTarget = _currentRelatedTarget.reduce((acc, item) => {
        const isNameMatch = regexp.test(item.name);
        const filteredSFs = item.searchFields.filter((sf) => regexp.test(sf.displayName));
        if (isNameMatch || filteredSFs.length > 0) {
          acc.push({
            ...item,
            searchFields: isNameMatch ? item.searchFields : filteredSFs, // Include only matched searchFields
          });
        }
        return acc;
      }, []);
    }

    return _currentRelatedTarget.map((st: SearchTargetDTO) => (
      <CollapsiblePanel title={st.name} key={`st-${st.id}`} showEmpty={false}>
        {st.searchFields.map((sf: SearchFieldDTO) => {
          const targetField = defaultSelectedFields[selectedSearchTargetId]?.find(
            (field) => field.id === sf.id,
          );
          const displayField = displayFields.find(
            (field) => field.searchFieldId === sf.id,
          );
          if (
            displayField?.status !== SearchDisplayFieldStatus.HiddenForIDs &&
            searchFieldFilter.filter(displayField)
          ) {
            const isChecked = displayField !== undefined || pendingSelections.has(sf.id);
            return (
              <LabelRowWithActions
                isVisible={displayField?.status === SearchDisplayFieldStatus.Display}
                isChecked={isChecked}
                onCheckboxChange={handleSelectionChange}
                columnId={sf.id}
                columnTitle={sf.displayName}
                key={`sf-${sf.id}`}
                onVisibilityChange={visibilityChangeHandler}
                disabled={targetField?.mandatory}
                disableVisibilityChange={!displayField}
              />
            );
          }
        })}
      </CollapsiblePanel>
    ));
  }, [
    targetRelations,
    selectedSearchTargetId,
    searchFieldQuery,
    displayFields,
    isResultTab,
    searchFieldFilter,
    pendingSelections,
    defaultSelectedFields,
  ]);

  const filterSelectionHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = searchFieldsFilters.find((f) => f.value == parseInt(e.target.value));
    setSearchFieldFilter(selected);
  };

  const performSearchHandler = () => {
    setSearchAgain(true);
    hideModal();
  };

  const handleAddButtonClick = () => {
    if (pendingSelections.size === 0) {
      return;
    }

    let maxDisplayOrder = 0;
    displayFields.forEach((df) => {
      if (maxDisplayOrder <= df.displayOrder) {
        maxDisplayOrder = df.displayOrder + 1;
      }
    });

    const fieldsToAdd: ISearchDisplayFieldDTO[] = [];
    const fieldsRequiringSearch: ISearchFieldDTO[] = [];

    pendingSelections.forEach((fieldId) => {
      let searchField: ISearchFieldDTO = undefined;
      if (targetRelations[selectedSearchTargetId]) {
        targetRelations[selectedSearchTargetId].forEach((st) => {
          const sf = st.searchFields.find((sf) => sf.id === fieldId);
          if (sf) {
            searchField = sf;
          }
        });
      }

      if (searchField) {
        fieldsToAdd.push({
          searchFieldId: fieldId,
          columnHeader: searchField.fieldAndTableDisplayName,
          status: SearchDisplayFieldStatus.Display,
          displayOrder: maxDisplayOrder++,
        } as ISearchDisplayFieldDTO);

        if (
          isResultTab &&
          searchResult.rows.items.length &&
          !(searchField.fieldAndTableDisplayName in searchResult.rows.items[0])
        ) {
          fieldsRequiringSearch.push(searchField);
        }
      }
    });

    fieldsToAdd.forEach((field) => {
      dispatch(addDisplayField(field));
    });

    setPendingSelections(new Set());

    if (fieldsRequiringSearch.length > 0) {
      showModal({
        body: 'The system will perform search again because you are selecting new parameters.',
        onOk: performSearchHandler,
        heading: 'Customize Columns',
        okButtonText: 'Confirm',
        isCancelOnly: false,
        noButtonText: 'Cancel',
      });
    }
  };

  return (
    <div id="searchandfilter-search-panelcontentcontainer" className="p-2">
      <div id="searchandfilter-search-panelcontentcontainer-input" className="flex gap-5">
        <div
          id="searchandfilter-search-panelcontentcontainer-input-with-icon"
          className="w-4/5"
        >
          <InputWithIcon
            icon={
              <RMSvgIcon
                id="snf-input-search-icon"
                name="Search"
                height={15}
                width={15}
                fill="#222"
              />
            }
            value={searchFieldQuery}
            onChange={(e) => {
              setSearchFieldQuery(e.target.value);
            }}
            placeholder="Search Available Columns"
          />
        </div>
        <SelectInput
          className="w-1/5"
          options={searchFieldsFilters}
          value={searchFieldFilter.value}
          onChange={filterSelectionHandler}
        />
      </div>
      <div
        id="searchandfilter-search-panelcontentcontainer-targetrelationsoptionslist"
        className="h-[72.5vh] max-h-[73vh] overflow-y-auto overflow-x-hidden"
      >
        {targetsRelationsOptionsList}
      </div>
      <div className="h-[6.5vh] content-center text-right">
        <FmlxButton
          type={FmlxButton.Type.POSITIVE}
          onClick={handleAddButtonClick}
          label="Add"
          disabled={pendingSelections.size === 0}
        />
      </div>
    </div>
  );
};

export default PanelContentContainer;
